import type { FetchError } from 'ofetch'
import type { AvailableRouterMethod, NitroFetchRequest, TypedInternalResponse } from 'nitropack'
import type { Ref } from 'vue'
import type { AsyncData, KeysOf, PickFrom } from 'nuxt/dist/app/composables/asyncData'
import type { UseFetchOptions } from '~/schema/UseFetchOptionSchema'
import type { ResponseEnum } from '~/schema/response.schema'
import { defaultLimitPagination } from '~/constants/pagination.constants'

export type FetchResult<ReqT extends NitroFetchRequest, M extends AvailableRouterMethod<ReqT>> = TypedInternalResponse<ReqT, unknown, M>;

export const useFetchAPI = async <ResT = void, ErrorT = FetchError, ReqT extends NitroFetchRequest = NitroFetchRequest, Method extends AvailableRouterMethod<ReqT> = 'get' extends AvailableRouterMethod<ReqT> ? 'get' : AvailableRouterMethod<ReqT>, _ResT = ResT extends void ? FetchResult<ReqT, Method> : ResT, DataT = _ResT, PickKeys extends KeysOf<DataT> = KeysOf<DataT>>(url: Ref<ReqT> | ReqT | (() => ReqT), options?: UseFetchOptions, page?: Ref<number>, limit?: Ref<number>): Promise<AsyncData<PickFrom<DataT, PickKeys> | null, ErrorT | null>> => {
  const config = useRuntimeConfig().public
  const query = {}

  if (page?.value !== undefined) {
    Object.assign(query, {
      page: page?.value ?? 1
    })
  }

  if (limit?.value !== undefined) {
    Object.assign(query, {
      limit: limit?.value ?? defaultLimitPagination
    })
  }

  return await useFetch<DataT, ErrorT, ReqT, any, _ResT, DataT, PickKeys>(url, {
    lazy: true,
    server: false,
    baseURL: config.api_base_url,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'include',
    ...options,
    query: {
      ...(options?.query !== undefined && options.query),
      ...query
    }
  }).then((data) => {
    // @ts-ignore
    if (data.error.value && Object.keys(data.error.value).includes('statusCode') && data.error.value.statusCode !== 401) {
      throw data.error.value
    }

    return data
  })
}

interface CustomRequestInit extends RequestInit {
    query?: Record<string, any>,
    json?: RequestInit['body'] | Record<string, any>
}

export const useCustomFetch = async <DataT>(url: string, options?: CustomRequestInit, page?: number, limit?: number, type: ResponseEnum = ResponseType.json) => {
  const config = useRuntimeConfig().public

  const newURL = new URL(`${config.api_base_url}${url}`)

  const query = new URLSearchParams({ ...(options?.query !== undefined && options.query) })

  if (page !== undefined) {
    query.append('page', page.toString() ?? '1')
  }

  if (limit !== undefined) {
    query.append('limit', limit.toString() ?? defaultLimitPagination.toString())
  }

  newURL.search = query.toString()

  if (options?.json !== undefined && options?.json !== null) {
    options.body = JSON.stringify(options.json)
  }

  let headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }

  if (options?.headers) {
    headers = {
      ...headers,
      ...options.headers
    }
  }

  return await fetch(newURL, {
    headers,
    credentials: 'include',
    ...options
  }).then(async (response) => {
    const data: Promise<DataT> = response[type]()

    if (response.ok) {
      return data
    }

    throw await data
  })
}
